import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AttributeSchemaTypeEnum, IAttribute } from "../../../services/attribute-data-service/Models";
import { ObjectPropertiesFormValues } from "../../object-properties/ObjectPropertiesUtils";
import { TagPropertyFormValues } from "../../tag-properties/TagPropertyUtils";
import FormDatePicker from "../form-date-picker/FormDatePicker";
import FormInput from "../form-input/FormInput";
import FormPropertySection from "../form-property-section/FormPropertySection";
import FormProperty from "../form-property/FormProperty";
import { FormFieldPropsBase } from "../form/Form";
import FormSelect from "../form-select/FormSelect";
import { Alert, IconButton, MenuItem, Stack, Tooltip } from "@mui/material";
import FormComboBox from "../form-combo-box/FormComboBox";
import { Clear } from "@mui/icons-material";
import { Ref, forwardRef, useImperativeHandle } from "react";
import ApplyAttributeDialog, { bindApplyAttributeDialog, useApplyAttributeDialogState } from "../../apply-attribute-dialog/ApplyAttributeDialog";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import { useAttributeSchemas } from "../../../queries/attributes/UseAttributeSchemas";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useUserContext } from "../../../contexts/user-context/UserContext";

export type FormCustomAttributesType = {
    applyAttribute: () => void;
};

export type FormCustomAttributesProps = FormFieldPropsBase & {
    organizationID: number;
    groupID: number;
    objectType: eLynxObjectTypeEnum;
    //index: number;
    //onAction?: (action: FormAttachmentActionCardEnum, index: number, attachment: IAttachment) => void;
};

function FormCustomAttributes({
    organizationID,
    groupID,
    objectType,
    //name,
    //id,
    readOnly,
    //disabled,
    //index,
    //onAction
}: FormCustomAttributesProps, ref: Ref<unknown> | undefined) {

    const [userState] = useUserContext();
    const { t } = useTranslation();
    const { confirm } = useConfirmation();
    const applyAttributeDialog = useApplyAttributeDialogState();


    const {
        control,
        getValues
    } = useFormContext<ObjectPropertiesFormValues | TagPropertyFormValues>();

    const { data: schemas } = useQueryNotifications(useAttributeSchemas)({
        groupID,
        organizationID
    });


    const {
        fields: attributes,
        // append: appendAttributes,
        remove: removeAttribute,
        insert: insertAttribute,
        // move
    } = useFieldArray({
        control: control,
        name: "attributes",
        keyName: "fieldKey"
    });


    function onRemoveAttribute(ndx: number) {

        removeAttribute(ndx);

    }

    async function onApplyAttribute() {

        if ((schemas?.length ?? 0) === 0) {

            await confirm({
                dialogTitle: '',
                confirmationBodyComponent: <Alert severity="warning">{t('attributeSchema.noAvailable', { objectType: getObjectTypeName(objectType) })}</Alert>,
                yesButtonText: t("buttons.close"),
                showConfirmationText: true,
                showYesButton: true,
            });
        }

        const result = await applyAttributeDialog.open({
            currentAttributes: attributes ?? []
        });

        // SPR 2024-02-06 - Bug 51130: Apply Attribute Bug
        // This doesn't work right because sort modifies the array
        // in place. That messes up the original indices.
        // if (result.reason === 'ok') {

        //     const newAttribute = result.values?.attribute as IAttribute;
        //     appendAttributes(newAttribute);

        //     const currentAttributes = getValues('attributes');

        //     const sortedAttributes = currentAttributes.sort((a, b) => a.an.localeCompare(b.an));

        //     sortedAttributes.forEach((o, ndx) => {

        //         const currentIndex = currentAttributes.findIndex(v => v.aid === o.aid);

        //         if (currentIndex !== ndx) {
        //             move(currentIndex, ndx);
        //         }
        //     });

        // }

        if (result.reason === 'ok') {
            const newAttribute = result.values?.attribute as IAttribute;
            const currentAttributes = getValues('attributes');
            
            // Insert the new attribute at the correct location
            const insertIndex = currentAttributes.findLastIndex(a => a.an.localeCompare(newAttribute.an) < 0) + 1;
            insertAttribute(insertIndex, newAttribute);
        }
    }


    useImperativeHandle<unknown, FormCustomAttributesType>(
        ref,
        () => ({
            applyAttribute: onApplyAttribute
        })
    );


    const _validateMaxWholeDigits = (value: string, wholeDigits: number | undefined): boolean => {

        // value can be null or undefined due to bad attribute data, so we need to check for that
        if (wholeDigits === undefined || !value) {
            return true;
        }

        const str = value.toString().trim().replace('-', '');
        const ndx = str.indexOf('.');
        return ndx !== -1 ?
            str.substring(0, ndx).length <= wholeDigits :
            str.length <= wholeDigits;
    };


    return (

        <>
            {
                attributes.length > 0 ?
                    <FormPropertySection label={t("properties.customAttributes")}>

                        {attributes.map((field, index) => (

                            <FormProperty key={field.fieldKey}>
                                <Stack direction='row'>
                                    {
                                        field.attrs?.stype === AttributeSchemaTypeEnum.Date ?
                                            <FormDatePicker
                                                id={field.aid.toString()}
                                                showID={userState.user?.isElynxAdmin}
                                                testId={field.an}
                                                name={`attributes.${index}.avdate`}
                                                label={field.an}
                                                views={['month', 'day', 'year']}
                                                readOnly={readOnly}
                                                sx={{ width: "100%", position: 'relative' }}
                                            />
                                            :
                                            field.attrs?.stype === AttributeSchemaTypeEnum.PredefinedValue ?

                                                <FormSelect
                                                    id={field.aid.toString()}
                                                    showID={userState.user?.isElynxAdmin}
                                                    name={`attributes.${index}.av`}
                                                    label={field.an}
                                                    readOnly={readOnly}
                                                    sx={{ width: "90%" }}
                                                >
                                                    {field.attrs?.vopts?.map((o, ndx) =>
                                                        <MenuItem key={ndx} value={o.val}>
                                                            {o.val}
                                                        </MenuItem>

                                                    )}
                                                </FormSelect>
                                                :
                                                field.attrs?.stype === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText ?
                                                    <FormComboBox
                                                        id={field.aid.toString()}
                                                        showID={userState.user?.isElynxAdmin}
                                                        name={`attributes.${index}.av`}
                                                        label={field.an}
                                                        readOnly={readOnly}
                                                        sx={{ width: "90%" }}
                                                        options={field.attrs?.vopts?.map(o => o.val) ?? []}
                                                        rules={{
                                                            required: false,
                                                            maxLength: 128,
                                                            message: t('properties.maxAttributeLength')
                                                        }}
                                                    />
                                                    :
                                                    <FormInput
                                                        id={field.aid.toString()}
                                                        showID={userState.user?.isElynxAdmin}
                                                        testId={field.an}
                                                        name={`attributes.${index}.av`}
                                                        label={field.an}
                                                        rules={{
                                                            required: false,
                                                            maxLength: 128,
                                                            message: t('properties.maxAttributeLength'),
                                                            validate: field.attrs?.stype === AttributeSchemaTypeEnum.Numeric ? (value: string) => !_validateMaxWholeDigits(value, field.attrs?.wd) ?
                                                                t('attributeSchema.maxWholeDigits', { value: field.attrs?.wd }) :
                                                                true
                                                                : undefined
                                                        }}
                                                        readOnly={readOnly}
                                                        sx={{ width: "90%" }}
                                                        decimals={field.attrs?.stype === AttributeSchemaTypeEnum.Numeric ? field.attrs?.dd : undefined}
                                                        numeric={field.attrs?.stype === AttributeSchemaTypeEnum.Numeric ? true : undefined}
                                                    />
                                    }

                                    {!readOnly &&
                                        <IconButton
                                            edge="start"
                                            color="inherit"
                                            aria-label={t("attributeSchema.removeAttr") as string}
                                            sx={{
                                                marginLeft: 1
                                            }}
                                            onClick={() => { onRemoveAttribute(index); }}>
                                            <Tooltip title={t("attributeSchema.removeAttr") as string}>
                                                <Clear />
                                            </Tooltip>
                                        </IconButton>
                                    }
                                </Stack>

                            </FormProperty>
                        ))}

                    </FormPropertySection> :
                    null
            }

            <ApplyAttributeDialog {...bindApplyAttributeDialog(applyAttributeDialog)} />

        </>

    );
}

export default forwardRef(FormCustomAttributes);